import { KeyboardBackspace, SettingsOutlined } from "@mui/icons-material";
import { Avatar, Box, CardContent, Grid2, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { stringAvatar } from "../../../../utils/helper";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { useGetMyParents } from "@services/student/parents";
import { useStudentProfileDetails } from "../../../../hooks/useStudentProfileDetails";
import { useStudentCurriculumCustomFields } from "../../../../hooks/useStudentCurriculumCustomFields";
import moment from "moment";
import StudentUserModel from "@components/models/user/StudentUserModel";
import AiTutorVoiceSettings from "./AiTutorVoiceSettings";
import PageHeader from "@components/shared/ui/PageHeader";
import SurfaceCard from "@components/shared/ui/SurfaceCard";

export default function StudentProfile() {
  const user = useAuth().profile as StudentUserModel;

  // Linked parents live in the `student_parents` junction (not on the
  // user profile). `user.details.parents` was always undefined post-
  // Supabase migration because AuthContext.fetchProfile doesn't embed
  // them — the dedicated hook joins through the junction directly.
  const { rows: parents } = useGetMyParents();

  // The page used to read `user.details.id_number`, `user.details.DOB`,
  // `user.details.student_grade_level.*`, etc., but `users.details`
  // doesn't exist as a column — student fields are on the `students`
  // table and the grade/curriculum linkage lives in `student_grade_levels`.
  // Surfaced 2026-05-16 in bug 3d623af7. The dedicated hook pulls the
  // real values.
  const { details } = useStudentProfileDetails();

  // "Program" and any other tenant attribute are curriculum custom fields,
  // resolved from the org's field definitions + this student's curriculum.
  const { fields: customFields } = useStudentCurriculumCustomFields(
    (user as any)?.student_id ?? null,
  );

  return (
    <Stack alignItems="start" spacing={4} sx={{ width: '100%' }}>
      <Grid2 py={1.5} container width="100%" alignItems="center" wrap="nowrap">
        <IconButton color="inherit" component={Link} to={`/${user?.base_role}`}>
          <KeyboardBackspace />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <PageHeader
            eyebrow="Student"
            title="My Profile"
            actions={
              <PopupState variant="popover" popupId="popup-menu">
                {(popupState) => (
                  <React.Fragment>
                    <IconButton {...bindTrigger(popupState)}>
                      <SettingsOutlined sx={{ fontSize: 32 }} />
                    </IconButton>
                    <Menu
                      {...bindMenu(popupState)}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      <MenuItem component={Link} to="/student/profile/edit" onClick={popupState.close}>
                        Edit Profile
                      </MenuItem>
                      <MenuItem
                        component={Link}
                        to="/student/profile/change-password"
                        onClick={popupState.close}
                      >
                        Change Password
                      </MenuItem>
                    </Menu>
                  </React.Fragment>
                )}
              </PopupState>
            }
          />
        </Box>
      </Grid2>

      <SurfaceCard
        sx={{ overflow: "visible", alignSelf: "stretch", p: 4 }}
      >
        <CardContent>
          <Grid2 container sx={{ alignItems: "center", flexWrap: "nowrap" }} spacing={2}>
            <Avatar
              src={user?.avatar_url ?? undefined}
              {...stringAvatar(`${user?.first_name} ${user?.last_name}`)}
              sx={{ width: "3.2rem", height: "auto", aspectRatio: 1 }}
            />
            <Grid2>
              <PageHeader title={`${user?.first_name} ${user?.last_name}`} />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} columnSpacing={4} sx={{ mt: 2 }}>
            <Info label="Email ID" val={user?.email} />
            <Info label="ID Number" val={details?.id_number ?? undefined} />
            <Info label="Date of Birth" val={details?.dob ? moment(details.dob).format("MMMM DD, YYYY") : "-"} />
            <Info label="Curriculum" val={details?.curriculum_name ?? "-"} />
            {customFields.map((f) => (
              <Info key={f.key} label={f.label} val={f.value || "-"} />
            ))}
            <Info label="Grade Level" val={details?.grade_level ?? "-"} />
            <Info label="Class" val={details?.class_name ?? "—"} />
            <Info label="School Year" val={details?.school_year_name ?? "-"} />
            <Info label="Expiring On" val={details?.expiring_on ? moment(details.expiring_on).format("MMMM DD, YYYY") : "—"} />
            <Info label="Role" val={user?.base_role} capitalized/>
            <Info label="Status" val="Active" />
          </Grid2>
        </CardContent>
      </SurfaceCard>

      {parents.map((p, index) => <SurfaceCard
        key={index}
        sx={{ overflow: "visible", alignSelf: "stretch", p: 4 }}
      >
        <CardContent>
          <Grid2 container sx={{ alignItems: "center", flexWrap: "nowrap" }} spacing={2}>
            <Grid2>
              <PageHeader title={`Parent ${index + 1}`} />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} columnSpacing={4} sx={{ mt: 2 }}>
            <Info label="Name" val={`${p?.first_name ?? ''} ${p?.last_name ?? ''}`.trim() || '-'} />
            <Info label="Email ID" val={p?.email || '-'} />
            <Info label="Role" val="parent" capitalized/>
            <Info label="Relationship" val={(p as any)?.relationship ?? "—"} />
            <Info label="Contact Number" val={p?.mobile_number ?? "—"} />
          </Grid2>
        </CardContent>
      </SurfaceCard>)}

      <AiTutorVoiceSettings />
    </Stack>
  );
}


const Info = ({ label, val, capitalized = false }: { label: string; val?: string, capitalized?: boolean }) => (
  <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
    <Grid2 container alignItems="baseline" spacing={1} wrap="nowrap">
      <Typography variant="weak" whiteSpace="nowrap">{label}:</Typography>
      <Typography variant="subtitle2" className="truncate" textTransform={capitalized ? 'capitalize' : undefined}>{val || "-"}</Typography>
    </Grid2>
  </Grid2>
);

